import { useState, type FormEvent } from "react";
import { Check, Copy, FileText, Save, Settings2 } from "lucide-react";
import { Button } from "../ui";
import { useApp } from "../../context/AppContext";
import { api } from "../../lib/api";
import type { Contact } from "../../types";

const PAYMENT_OPTIONS = [
  "70% 定金 + 30% 尾款（发货前付清）",
  "50% 定金 + 50% 尾款（发货前付清）",
  "30% 定金 + 70% 尾款（见提单副本付清）",
  "100% 定金（样品/小单）",
  "T/T 电汇"
];

const TRADE_TERMS = ["FOB 深圳", "EXW 工厂", "CIF", "DDP"];

interface QuoteTemplate {
  company: string;
  contact: string;
  contactInfo: string;
  defaultPayment: string;
  defaultLeadTime: string;
  footer: string;
}

const DEFAULT_TEMPLATE: QuoteTemplate = {
  company: "",
  contact: "",
  contactInfo: "",
  defaultPayment: PAYMENT_OPTIONS[0] ?? "70% 定金 + 30% 尾款（发货前付清）",
  defaultLeadTime: "打样 7-10 天，大货 35-45 天",
  footer: "感谢询价！如有任何问题，欢迎随时联系。"
};

function loadTemplate(): QuoteTemplate {
  try {
    const raw = localStorage.getItem("eo_quote_template");
    if (raw) return { ...DEFAULT_TEMPLATE, ...(JSON.parse(raw) as Partial<QuoteTemplate>) };
  } catch {
    /* ignore */
  }
  return DEFAULT_TEMPLATE;
}

function saveTemplate(template: QuoteTemplate) {
  try {
    localStorage.setItem("eo_quote_template", JSON.stringify(template));
  } catch {
    /* ignore */
  }
}

function formatDate() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function QuoteModal({
  contact,
  onClose
}: {
  contact: Contact;
  onClose: () => void;
}) {
  const { data, mutate, notify } = useApp();
  const [generated, setGenerated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [template, setTemplate] = useState<QuoteTemplate>(loadTemplate);
  const [editingTemplate, setEditingTemplate] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (key: string) => String(form.get(key) ?? "").trim();
    const unitPrice = text("unitPrice");
    const quantity = text("quantity");
    const currency = text("currency") || "USD";
    const tradeTerm = text("tradeTerm") || "FOB 深圳";
    const total =
      unitPrice && quantity && !Number.isNaN(Number(unitPrice)) && !Number.isNaN(Number(quantity))
        ? (Number(unitPrice) * Number(quantity)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "";

    const lines = [
      template.company ? template.company.toUpperCase() : "",
      "═══════════ QUOTATION ═══════════",
      `客户 CUSTOMER : ${contact.name}${contact.organization ? ` (${contact.organization})` : ""}`,
      `日期 DATE     : ${formatDate()}`,
      `有效期 VALID  : ${text("validity") || "15 天"}`,
      "──────────────────────────────",
      text("code") ? `款号 ITEM NO.  : ${text("code")}` : "",
      text("title") ? `款式 ITEM      : ${text("title")}` : "",
      text("fabric") ? `面料 FABRIC   : ${text("fabric")}${text("weight") ? ` (${text("weight")} gsm)` : ""}` : "",
      text("color") ? `颜色 COLOR    : ${text("color")}` : "",
      quantity ? `数量 QUANTITY : ${quantity} 件` : "",
      unitPrice ? `单价 UNIT PRICE: ${currency} ${unitPrice} / 件 (${tradeTerm})` : "",
      total ? `金额 AMOUNT   : ${currency} ${total}` : "",
      "MOQ           : 柔性快反 + 大货批量",
      "──────────────────────────────",
      `交期 LEAD TIME: ${text("leadTime") || template.defaultLeadTime}`,
      `付款 PAYMENT   : ${text("payment") || template.defaultPayment}`,
      text("note") ? `备注 NOTE     : ${text("note")}` : "",
      "═══════════════════════════════",
      template.contact ? `报价人: ${template.contact}` : "",
      template.contactInfo ? template.contactInfo : "",
      template.footer ? template.footer : ""
    ].filter(Boolean);
    setGenerated(lines.join("\n"));
    setCopied(false);
  };

  const saveTemplateForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next: QuoteTemplate = {
      company: String(form.get("company") ?? "").trim(),
      contact: String(form.get("contact") ?? "").trim(),
      contactInfo: String(form.get("contactInfo") ?? "").trim(),
      defaultPayment: String(form.get("defaultPayment") ?? PAYMENT_OPTIONS[0]),
      defaultLeadTime: String(form.get("defaultLeadTime") ?? "").trim(),
      footer: String(form.get("footer") ?? "").trim()
    };
    setTemplate(next);
    saveTemplate(next);
    setEditingTemplate(false);
  };

  const copy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  const saveToOrder = async () => {
    if (!generated) return;
    const order = data?.customerOrders.find((item) => item.contact_id === contact.id);
    if (!order) {
      notify("先给该客户创建订单，报价才能存档到订单里", "info");
      return;
    }
    const stamped = `\n【报价 ${formatDate()}】\n${generated}`;
    await mutate(
      () =>
        api.update("customer-orders", order.id, {
          contactId: order.contact_id,
          title: order.title,
          images: JSON.parse(order.images_json),
          files: JSON.parse(order.files_json ?? "[]"),
          requirements: order.requirements,
          amount: (order.amount_minor / 100).toString(),
          currency: order.currency,
          quantity: order.quantity,
          status: order.status,
          nextAction: order.next_action,
          nextFollowup: order.next_followup,
          note: `${order.note ?? ""}${stamped}`
        }),
      "报价已存档到订单备注"
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 1600);
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal quote-modal">
        <div className="modal__header">
          <div>
            <span className="eyebrow">QUOTATION · 报价单</span>
            <h2>给 {contact.name} 报价</h2>
            <p>按模板生成，可复制粘贴到邮件或 WhatsApp。</p>
          </div>
          <div className="quote-header-actions">
            <button className="quote-template-btn" onClick={() => setEditingTemplate((current) => !current)} title="报价模板设置">
              <Settings2 size={15} /> 模板
            </button>
            <button className="modal__close" onClick={onClose} aria-label="关闭">✕</button>
          </div>
        </div>

        {editingTemplate ? (
          <form onSubmit={(event) => void saveTemplateForm(event)} className="quote-template-form">
            <div className="field field--full"><span>公司抬头（显示在报价顶部）</span><input name="company" defaultValue={template.company} placeholder="如 Jack Garments Co., Ltd." /></div>
            <div className="field"><span>报价人</span><input name="contact" defaultValue={template.contact} placeholder="你的名字" /></div>
            <div className="field"><span>联系方式</span><input name="contactInfo" defaultValue={template.contactInfo} placeholder="WhatsApp / 邮箱" /></div>
            <div className="field"><span>默认付款条款</span><select name="defaultPayment" defaultValue={template.defaultPayment}>{PAYMENT_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></div>
            <div className="field"><span>默认交期</span><input name="defaultLeadTime" defaultValue={template.defaultLeadTime} /></div>
            <div className="field field--full"><span>落款/备注</span><textarea name="footer" rows={2} defaultValue={template.footer} /></div>
            <div className="modal__actions">
              <Button variant="secondary" onClick={() => setEditingTemplate(false)}>返回报价</Button>
              <Button type="submit" icon={<Save size={15} />}>保存模板</Button>
            </div>
          </form>
        ) : !generated ? (
          <form onSubmit={(event) => void submit(event)} className="quote-form">
            <div className="field"><span>款号 / ITEM NO.</span><input name="code" placeholder="如 HO-001" /></div>
            <div className="field"><span>款式名称 *</span><input name="title" required placeholder="如 Flight Mode™ Hoodie" /></div>
            <div className="field"><span>面料</span><input name="fabric" placeholder="如 320gsm 摇粒绒" /></div>
            <div className="field"><span>克重 gsm</span><input name="weight" type="number" placeholder="如 320" /></div>
            <div className="field"><span>颜色</span><input name="color" placeholder="如 黑色 / 灰色" /></div>
            <div className="field"><span>数量（件）*</span><input name="quantity" type="number" min="1" required placeholder="如 300" /></div>
            <div className="field"><span>单价 *</span><input name="unitPrice" type="number" min="0" step="0.01" required placeholder="如 12.50" /></div>
            <div className="field"><span>币种</span><select name="currency" defaultValue="USD"><option>USD</option><option>CNY</option><option>EUR</option><option>HKD</option><option>JPY</option></select></div>
            <div className="field"><span>贸易条款</span><select name="tradeTerm" defaultValue="FOB 深圳">{TRADE_TERMS.map((term) => <option key={term}>{term}</option>)}</select></div>
            <div className="field"><span>交期</span><input name="leadTime" placeholder="如 打样 7-10 天，大货 35-45 天" /></div>
            <div className="field"><span>付款条款</span><select name="payment">{PAYMENT_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></div>
            <div className="field"><span>报价有效期</span><input name="validity" defaultValue="15 天" /></div>
            <div className="field field--full"><span>备注</span><textarea name="note" rows={2} placeholder="如 含印花绣花工艺、含包装，不含运费" /></div>
            <div className="modal__actions">
              <Button variant="secondary" onClick={onClose}>取消</Button>
              <Button type="submit" icon={<FileText size={15} />}>生成报价</Button>
            </div>
          </form>
        ) : (
          <div className="quote-result">
            <textarea readOnly value={generated} rows={18} spellCheck={false} />
            <div className="modal__actions">
              <Button variant="secondary" onClick={() => setGenerated(null)}>修改</Button>
              <Button onClick={() => void saveToOrder()} icon={saved ? <Check size={15} /> : <Save size={15} />}>
                {saved ? "已存档" : "存到订单"}
              </Button>
              <Button onClick={() => void copy()} icon={copied ? <Check size={15} /> : <Copy size={15} />}>
                {copied ? "已复制" : "复制报价"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
